import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  InputNumber,
  Button,
  Tag,
  Modal,
  Form,
  message,
  Switch,
  Typography,
  Row,
  Col,
  Alert,
  Tree,
  Select,
  Collapse,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
  LinkOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { agentAPI } from '../services/api';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const DATE_FORMAT = 'YYYY-MM-DD';
// 起止日期最大跨度（含起止两端）为 30 天，即天数差 ≤ 29，与后端校验一致。
const MAX_DATE_RANGE_DAYS = 30;

function formatCurrency(value) {
  return `¥${Number(value || 0).toFixed(2)}`;
}

// Sales display is separate from the original cash/commission base. Keep
// explicit zero values and support older API responses without sales fields.
function getSalesAmount(agent, period) {
  return Number(agent[`${period}_sales`] ?? agent[`${period}_recharge_before`] ?? agent[`${period}_recharge`] ?? 0);
}

function getSelectedDay(dateValue) {
  const parsed = dateValue ? dayjs(dateValue) : dayjs();
  return parsed.isValid() ? parsed : dayjs();
}

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

function copyTextToClipboard(text) {
  const value = String(text || '');
  if (!value) {
    return Promise.reject(new Error('empty copy text'));
  }

  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(value).catch(() => fallbackCopyText(value));
  }

  return fallbackCopyText(value);
}

function fallbackCopyText(text) {
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      copied ? resolve() : reject(new Error('copy command failed'));
    } catch (error) {
      document.body.removeChild(textarea);
      reject(error);
    }
  });
}

function buildGroupedAgentRows(nodes = [], depth = 0, parentName = null) {
  return nodes
    .filter(node => !node.is_user)
    .map(node => {
      const commissionRate = Number(node.commission_rate || 0);
      const selectedDayBefore = Number(node.selected_day_recharge_before ?? node.selected_day_recharge ?? 0);
      const todayBefore = Number(node.today_recharge_before ?? node.today_recharge ?? 0);
      const yesterdayBefore = Number(node.yesterday_recharge_before ?? node.yesterday_recharge ?? 0);
      const totalBefore = Number(node.total_recharge_before ?? node.total_recharge ?? 0);
      const children = buildGroupedAgentRows(node.children || [], depth + 1, node.agent_name);

      return {
        ...node,
        depth,
        parent_name: parentName,
        selected_day_sales: getSalesAmount(node, 'selected_day'),
        today_sales: getSalesAmount(node, 'today'),
        yesterday_sales: getSalesAmount(node, 'yesterday'),
        total_sales: getSalesAmount(node, 'total'),
        selected_day_recharge_before: selectedDayBefore,
        selected_day_recharge_after: Number(node.selected_day_recharge_after ?? selectedDayBefore * (commissionRate / 100)),
        today_recharge_before: todayBefore,
        today_recharge_after: Number(node.today_recharge_after ?? todayBefore * (commissionRate / 100)),
        yesterday_recharge_before: yesterdayBefore,
        yesterday_recharge_after: Number(node.yesterday_recharge_after ?? yesterdayBefore * (commissionRate / 100)),
        total_recharge_before: totalBefore,
        total_recharge_after: Number(node.total_recharge_after ?? totalBefore * (commissionRate / 100)),
        children: children.length > 0 ? children : undefined
      };
    });
}

function findAgentNodeById(nodes, agentId) {
  for (const node of nodes) {
    if (Number(node.id) === Number(agentId)) {
      return node;
    }
    if (node.children?.length) {
      const matched = findAgentNodeById(node.children, agentId);
      if (matched) {
        return matched;
      }
    }
  }
  return null;
}

function filterGroupedAgentRows(nodes, currentFilters) {
  const keyword = (currentFilters.keyword || '').trim().toLowerCase();
  const statusFilter = currentFilters.status;

  return nodes.reduce((result, node) => {
    const filteredChildren = filterGroupedAgentRows(node.children || [], currentFilters);
    const keywordMatched = !keyword || [
      node.agent_name,
      node.username,
      node.invite_code,
      node.contact,
      node.parent_name
    ].some(value => String(value || '').toLowerCase().includes(keyword));
    const statusMatched = statusFilter === undefined || statusFilter === '' || Number(node.status) === Number(statusFilter);

    if ((keywordMatched && statusMatched) || filteredChildren.length > 0) {
      result.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : undefined
      });
    }

    return result;
  }, []);
}

function flattenAgentOptions(nodes = [], depth = 0) {
  return nodes.reduce((result, node) => {
    result.push({
      id: node.id,
      depth,
      label: `${'-- '.repeat(depth)}${node.agent_name} (ID: ${node.id})`
    });

    if (Array.isArray(node.children) && node.children.length > 0) {
      result.push(...flattenAgentOptions(node.children, depth + 1));
    }

    return result;
  }, []);
}

function flattenVisibleAgentRows(nodes = []) {
  return nodes.reduce((result, node) => {
    result.push(node);
    if (Array.isArray(node.children) && node.children.length > 0) {
      result.push(...flattenVisibleAgentRows(node.children));
    }
    return result;
  }, []);
}

function AgentManagement() {
  const [filters, setFilters] = useState({
    keyword: '',
    status: undefined,
    parent_id: undefined,
    date_start: dayjs().format(DATE_FORMAT),
    date_end: dayjs().format(DATE_FORMAT)
  });
  // 记录 RangePicker 选择过程中的临时端点，用于跨度守卫（disabledDate）。
  const [rangePickerDates, setRangePickerDates] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalUsers: 0
  });
  const [agentTree, setAgentTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const agentCardRefs = useRef({});

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await agentAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const loadAgentTree = useCallback(async (
    dateStart = filters.date_start,
    dateEnd = filters.date_end
  ) => {
    setTreeLoading(true);
    try {
      const response = await agentAPI.getTree({ date_start: dateStart, date_end: dateEnd });
      console.log('Agent tree response:', response.data);
      setAgentTree(response.data.data);
    } catch (error) {
      console.error('Load agent tree error:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      message.error(`加载代理树失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setTreeLoading(false);
    }
  }, [filters.date_start, filters.date_end]);

  useEffect(() => {
    loadAgentTree(filters.date_start, filters.date_end);
  }, [filters.date_start, filters.date_end, loadAgentTree]);

  const handleViewDetail = useCallback(async (
    agent,
    dateStart = filters.date_start,
    dateEnd = filters.date_end
  ) => {
    try {
      const response = await agentAPI.getDetail(agent.id, { date_start: dateStart, date_end: dateEnd });
      setAgentDetail(response.data.data);
      setCurrentAgent(agent);
      setDetailVisible(true);
    } catch (error) {
      console.error('Load agent detail error:', error);
      message.error('加载代理详情失败');
    }
  }, [filters.date_start, filters.date_end]);

  useEffect(() => {
    if (!detailVisible || !currentAgent?.id) {
      return;
    }

    handleViewDetail(currentAgent, filters.date_start, filters.date_end);
  }, [detailVisible, currentAgent, filters.date_start, filters.date_end, handleViewDetail]);

  const handleCreate = (parentAgent = null) => {
    createForm.resetFields();
    if (parentAgent) {
      createForm.setFieldValue('parent_id', parentAgent.id);
    }
    setCreateVisible(true);
  };

  const handleCreateSubmit = async (values) => {
    try {
      await agentAPI.create({
        ...values,
        commission_rate: Number(values.commission_rate ?? 0)
      });
      message.success('代理创建成功');
      setCreateVisible(false);
      createForm.resetFields();
      loadStats();
      loadAgentTree();
    } catch (error) {
      console.error('Create agent error:', error);
      message.error(error.response?.data?.message || '创建代理失败');
    }
  };

  const handleEdit = (agent) => {
    setCurrentAgent(agent);
    editForm.setFieldsValue({
      ...agent,
      commission_rate: agent.commission_rate !== undefined && agent.commission_rate !== null
        ? Number(agent.commission_rate)
        : undefined,
      status: agent.status === 1,
      // 人工代充(需求一)新列: 转为布尔供 Switch 使用
      is_recharge_agent: Number(agent.is_recharge_agent || 0) === 1,
      share_quota: Number(agent.share_quota || 0) === 1,
      chat_nickname: agent.chat_nickname || ''
    });
    setEditVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await agentAPI.update(currentAgent.id, {
        ...values,
        commission_rate: values.commission_rate !== undefined && values.commission_rate !== null
          ? Number(values.commission_rate)
          : values.commission_rate,
        status: values.status ? 1 : 0,
        // 人工代充(需求一)新列: 布尔转 0/1, 后端仅在传入时追加更新
        is_recharge_agent: values.is_recharge_agent ? 1 : 0,
        share_quota: values.share_quota ? 1 : 0,
        chat_nickname: values.chat_nickname !== undefined ? String(values.chat_nickname || '').trim() : undefined
      });
      message.success('代理信息更新成功');
      setEditVisible(false);
      editForm.resetFields();
      loadAgentTree();
    } catch (error) {
      console.error('Update agent error:', error);
      message.error(error.response?.data?.message || '更新代理失败');
    }
  };

  const handleDelete = (agent) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除代理"${agent.agent_name}"吗？`,
      onOk: async () => {
        try {
          await agentAPI.delete(agent.id);
          message.success('代理删除成功');
          loadStats();
          loadAgentTree();
        } catch (error) {
          console.error('Delete agent error:', error);
          message.error(error.response?.data?.message || '删除代理失败');
        }
      }
    });
  };

  const handleToggleBan = (agent) => {
    const isBan = agent.status === 1;
    Modal.confirm({
      title: isBan ? '确认封禁代理' : '确认解封代理',
      content: isBan
        ? `确定要封禁代理"${agent.agent_name}"吗？封禁后将禁止代理登录和玩家注册。`
        : `确定要解封代理"${agent.agent_name}"吗？解封后将恢复代理登录和玩家注册。`,
      okText: isBan ? '确认封禁' : '确认解封',
      okButtonProps: isBan ? { danger: true } : undefined,
      onOk: async () => {
        try {
          if (isBan) {
            await agentAPI.ban(agent.id);
            message.success('代理封禁成功');
          } else {
            await agentAPI.unban(agent.id);
            message.success('代理解封成功');
          }
          loadStats();
          loadAgentTree();
          if (detailVisible && currentAgent?.id === agent.id) {
            handleViewDetail(agent);
          }
        } catch (error) {
          console.error('Toggle agent ban error:', error);
          message.error(error.response?.data?.message || (isBan ? '代理封禁失败' : '代理解封失败'));
        }
      }
    });
  };

  const handleGenerateCode = async () => {
    try {
      const response = await agentAPI.generateCode();
      createForm.setFieldValue('invite_code', response.data.data.invite_code);
      message.success('邀请码生成成功');
    } catch (error) {
      console.error('Generate code error:', error);
      message.error('生成邀请码失败');
    }
  };

  const handleCopyCode = async (code) => {
    try {
      await copyTextToClipboard(code);
      message.success('邀请码已复制到剪贴板');
    } catch (error) {
      message.error('复制邀请码失败');
    }
  };

  const handleCopyRegisterLink = async (inviteCode) => {
    try {
      const registerUrl = `${window.location.origin}/register?inviteCode=${encodeURIComponent(inviteCode || '')}`;
      await copyTextToClipboard(registerUrl);
      message.success('注册链接已复制到剪贴板');
    } catch (error) {
      message.error('复制注册链接失败');
    }
  };

  const scrollToAgentCard = (agentId) => {
    const targetCard = agentCardRefs.current[String(agentId)];
    if (!targetCard) {
      return;
    }

    targetCard.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  const handleTreeSelect = (selectedKeys) => {
    const selectedKey = selectedKeys?.[0];
    if (!selectedKey) {
      return;
    }

    const matched = String(selectedKey).match(/^agent-(.+)$/);
    if (!matched) {
      return;
    }

    const agentId = String(matched[1]);
    setSelectedAgentId(agentId);
    scrollToAgentCard(agentId);
  };

  const groupedAgents = useMemo(() => {
    const baseRows = buildGroupedAgentRows(agentTree);
    const parentFilter = filters.parent_id;

    if (parentFilter !== undefined && parentFilter !== null && parentFilter !== '') {
      const targetNode = findAgentNodeById(baseRows, parentFilter);
      if (!targetNode) {
        return [];
      }
      return filterGroupedAgentRows([targetNode], filters);
    }

    return filterGroupedAgentRows(baseRows, filters);
  }, [agentTree, filters]);

  const agentOptions = useMemo(() => flattenAgentOptions(agentTree), [agentTree]);
  const visibleAgentRows = useMemo(() => flattenVisibleAgentRows(groupedAgents), [groupedAgents]);
  const rangeStart = useMemo(() => getSelectedDay(filters.date_start), [filters.date_start]);
  const rangeEnd = useMemo(() => getSelectedDay(filters.date_end), [filters.date_end]);
  const rangeValue = useMemo(() => [rangeStart, rangeEnd], [rangeStart, rangeEnd]);
  const isSingleDay = useMemo(() => rangeStart.isSame(rangeEnd, 'day'), [rangeStart, rangeEnd]);
  const selectedDateLabel = useMemo(() => (
    isSingleDay
      ? rangeStart.format(DATE_FORMAT)
      : `${rangeStart.format(DATE_FORMAT)} ~ ${rangeEnd.format(DATE_FORMAT)}`
  ), [isSingleDay, rangeStart, rangeEnd]);
  const selectedDateShortLabel = useMemo(() => (
    isSingleDay
      ? rangeStart.format('MM-DD')
      : `${rangeStart.format('MM-DD')} ~ ${rangeEnd.format('MM-DD')}`
  ), [isSingleDay, rangeStart, rangeEnd]);
  const treeData = useMemo(() => {
    const buildNodes = (nodes = []) => nodes.map((node) => ({
      key: `agent-${node.id}`,
      title: (
        <div className="agent-tree-node">
          <div className="agent-tree-node-main">
            <div className="agent-tree-node-title-row">
              <span className="agent-tree-node-name">{node.agent_name}</span>
              <Tag color={node.status === 1 ? 'green' : 'red'}>
                {node.status === 1 ? '正常' : '封禁'}
              </Tag>
            </div>
            <div className="agent-tree-node-meta">
              <span>ID {node.id}</span>
              <span>{node.parent_name || '总代理'}</span>
              <span>{node.user_count || 0} 玩家</span>
            </div>
          </div>
          <div className="agent-tree-node-amounts">
            <span>{selectedDateShortLabel} {formatCurrency(node.selected_day_sales)}</span>
            <span>总计 {formatCurrency(node.total_sales)}</span>
          </div>
        </div>
      ),
      children: buildNodes(node.children || [])
    }));

    return buildNodes(groupedAgents);
  }, [groupedAgents, selectedDateShortLabel]);

  const agentFlowColumns = useMemo(() => ([
    {
      title: '层级',
      dataIndex: 'depth',
      key: 'depth',
      width: 105,
      render: (depth, record) => (
        <Tag color={Number(depth) === 0 ? 'volcano' : Number(depth) === 1 ? 'gold' : 'blue'}>
          {record.agent_role_name || (Number(depth) === 0 ? '1级总推' : Number(depth) === 1 ? '2级总代' : '3级代理')}
        </Tag>
      )
    },
    {
      title: '代理账号',
      dataIndex: 'username',
      key: 'username',
      width: 210,
      render: (username, record) => renderTableStack(
        <Text code>{username || '-'}</Text>,
        record.agent_name || '-',
        `代理ID ${record.id}`
      )
    },
    {
      title: '上级代理',
      dataIndex: 'parent_name',
      key: 'parent_name',
      width: 150,
      render: (parentName) => parentName || '无（顶级）'
    },
    {
      title: `${selectedDateShortLabel} 团队流水`,
      dataIndex: 'selected_day_sales',
      key: 'selected_day_sales',
      width: 160,
      render: (amount) => <Text strong style={{ color: '#ea580c' }}>{formatCurrency(amount)}</Text>
    },
    {
      title: '计佣基数（原口径）',
      dataIndex: 'selected_day_recharge_before',
      key: 'selected_day_recharge_before',
      width: 150,
      render: (amount) => <Text>{formatCurrency(amount)}</Text>
    },
    {
      title: '佣金比例',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      width: 105,
      render: (rate) => <Tag color="purple">{Number(rate || 0)}%</Tag>
    },
    {
      title: '佣金金额（原口径）',
      dataIndex: 'selected_day_recharge_after',
      key: 'selected_day_recharge_after',
      width: 140,
      render: (amount) => <Text strong style={{ color: '#16a34a' }}>{formatCurrency(amount)}</Text>
    },
    {
      title: '玩家 / 下级',
      key: 'counts',
      width: 130,
      render: (_, record) => `${Number(record.user_count || 0)} 人 / ${Number(record.sub_agent_count || 0)} 个`
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
          详情
        </Button>
      )
    }
  ]), [handleViewDetail, selectedDateShortLabel]);

  useEffect(() => {
    if (!selectedAgentId) {
      return;
    }

    const stillVisible = visibleAgentRows.some((row) => String(row.id) === String(selectedAgentId));
    if (!stillVisible) {
      setSelectedAgentId(null);
    }
  }, [selectedAgentId, visibleAgentRows]);

  const renderAgentActions = (record) => (
    <div className="agent-card-actions">
      <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
        详情
      </Button>
      <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
        编辑
      </Button>
      <Button size="small" icon={<UsergroupAddOutlined />} onClick={() => handleCreate(record)}>
        下级
      </Button>
      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
        删除
      </Button>
      <Button
        size="small"
        danger={record.status === 1}
        icon={record.status === 1 ? <LockOutlined /> : <CheckCircleOutlined />}
        onClick={() => handleToggleBan(record)}
      >
        {record.status === 1 ? '封禁' : '解封'}
      </Button>
    </div>
  );

  const detailColumns = [
    {
      title: '玩家信息',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 220,
      render: (_, record) => renderTableStack(
        record.game_name || record.user_name || '-',
        record.nick_name ? `昵称 ${record.nick_name}` : '未设置昵称',
        <Text code>{record.user_id}</Text>
      )
    },
    {
      title: '账号与绑定',
      dataIndex: 'bind_time',
      key: 'bind_time',
      width: 200,
      render: (time, record) => renderTableStack(
        record.user_name || '-',
        time ? new Date(time).toLocaleString('zh-CN') : '未记录绑定时间',
        record.game_user_id ? `游戏ID ${record.game_user_id}` : null
      )
    },
    {
      title: '角色进度',
      dataIndex: 'level',
      key: 'level',
      width: 180,
      render: (_, record) => renderTableStack(
        `等级 ${record.level || 0}`,
        `VIP ${record.vip_lv || 0}`,
        `战力 ${record.power || 0}`
      )
    }
  ];

  const subAgentDetailColumns = [
    {
      title: '代理信息',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 220,
      render: (_, record) => renderTableStack(
        record.agent_name || '-',
        record.username || '未设置登录账号',
        <Text code>{record.id}</Text>
      )
    },
    {
      title: '邀请码与状态',
      dataIndex: 'invite_code',
      key: 'invite_code',
      width: 200,
      render: (_, record) => renderTableStack(
        <Text code>{record.invite_code || '-'}</Text>,
        record.status === 1 ? '状态正常' : '状态封禁',
        <Tag color={record.status === 1 ? 'green' : 'red'}>{record.status === 1 ? '正常' : '封禁'}</Tag>
      )
    },
    {
      title: '成员规模',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 180,
      render: (_, record) => renderTableStack(
        `${record.user_count || 0} 人`,
        `下级代理 ${record.sub_agent_count || 0} 个`,
        record.create_time ? new Date(record.create_time).toLocaleString('zh-CN') : null
      )
    }
  ];

  const rechargeDetailColumns = [
    {
      title: '订单信息',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 220,
      render: (text, record) => renderTableStack(
        <Text code>{text || '-'}</Text>,
        record.payment_time ? new Date(record.payment_time).toLocaleString('zh-CN') : '未记录支付时间',
        record.user_id ? <Text code>{record.user_id}</Text> : null
      )
    },
    {
      title: '玩家与来源',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 220,
      render: (_, record) => renderTableStack(
        record.nick_name || record.user_name || '-',
        record.user_name || '未记录用户名',
        record.source_agent_name
          ? `${record.source_agent_name} (ID: ${record.source_agent_id})`
          : record.source_agent_id
            ? `代理ID: ${record.source_agent_id}`
            : '无来源代理'
      )
    },
    {
      title: '金额与状态',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 180,
      render: (_, record) => {
        const isSuccess = record.status === 1 || record.status === 3;
        return renderTableStack(
          <Text strong className="text-accent-success">¥{Number(record.total_amount || 0).toFixed(2)}</Text>,
          isSuccess ? '支付成功' : `状态 ${record.status}`,
          <Tag color={isSuccess ? 'green' : 'default'}>{isSuccess ? '成功' : record.status}</Tag>
        );
      }
    }
  ];

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">旭日之战 总代理后台</div>
          <div className="page-subtitle">主后台统一配置代理账号、最高权限、分成比例和充值流水，创建、删除、封禁和层级归属仍走原来的数据接口。</div>
        </div>
        <div className="page-header-actions">
          <Tag color="gold">最高权限配置</Tag>
          <Tag color="blue">可见代理 {visibleAgentRows.length}</Tag>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleCreate(null)}
          >
            新增代理账号
          </Button>
        </div>
      </div>

      <div className="agent-admin-hero">
        <div className="agent-admin-hero-main">
          <div className="agent-admin-kicker"><ApartmentOutlined /> GAME MASTER AGENCY</div>
          <div className="agent-admin-title">总代理、下级代理、玩家和流水保持独立口径。</div>
          <div className="agent-admin-text">这里负责给代理配置账号、上级归属、邀请码和分成比例；详情弹窗里单独查看下级玩家、下级代理和充值记录，避免流水混在一起。</div>
        </div>
        <div className="agent-admin-feature-grid">
          <div className="agent-admin-feature">
            <span>账号</span>
            <strong>新增 / 删除</strong>
          </div>
          <div className="agent-admin-feature">
            <span>权限</span>
            <strong>封禁 / 解封</strong>
          </div>
          <div className="agent-admin-feature">
            <span>分成</span>
            <strong>0-100%</strong>
          </div>
          <div className="agent-admin-feature">
            <span>流水</span>
            <strong>{selectedDateShortLabel}</strong>
          </div>
        </div>
      </div>

      <div className="compact-stat-row compact-stat-row-3">
        <div className="compact-stat">
          <div className="compact-stat-label">代理账号数</div>
          <div className="compact-stat-value text-accent-blue">{stats.totalAgents}</div>
          <div className="table-cell-secondary"><UsergroupAddOutlined /> 当前代理节点总数</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">活跃代理数</div>
          <div className="compact-stat-value text-accent-success">{stats.activeAgents}</div>
          <div className="table-cell-secondary"><CheckCircleOutlined /> 可正常登录与推广</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">绑定用户总数</div>
          <div className="compact-stat-value text-accent-purple">{stats.totalUsers}</div>
          <div className="table-cell-secondary"><ApartmentOutlined /> 代理体系下的总玩家数</div>
        </div>
      </div>

      <Card className="filter-card" title="账号筛选与分成配置">
        <div className="filter-bar-row">
          <div className="filter-bar-item filter-bar-search">
            <Input.Search
              placeholder="搜索代理名称、邀请码、联系方式"
              onSearch={(value) => {
                setFilters(prev => ({ ...prev, keyword: value }));
              }}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, keyword: e.target.value }));
              }}
              allowClear
              style={{ width: '100%' }}
            />
          </div>
          <div className="filter-bar-item">
            <Text type="secondary" className="filter-bar-label">上级代理</Text>
            <Select
              placeholder="筛选上级代理"
              allowClear
              style={{ width: 160 }}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, parent_id: value }));
              }}
            >
              {agentOptions.map(agent => (
                <Option key={agent.id} value={agent.id}>{agent.label}</Option>
              ))}
            </Select>
          </div>
          <div className="filter-bar-item">
            <Text type="secondary" className="filter-bar-label">日期范围</Text>
            <RangePicker
              allowClear={false}
              value={rangeValue}
              format={DATE_FORMAT}
              style={{ width: 280 }}
              disabledDate={(current) => {
                if (!current) {
                  return false;
                }
                const endpoints = rangePickerDates || [];
                const [fromDate, toDate] = endpoints;
                const tooLate = fromDate && current.diff(fromDate, 'days') > MAX_DATE_RANGE_DAYS - 1;
                const tooEarly = toDate && toDate.diff(current, 'days') > MAX_DATE_RANGE_DAYS - 1;
                return !!tooLate || !!tooEarly;
              }}
              onOpenChange={(open) => {
                setRangePickerDates(open ? [null, null] : null);
              }}
              onCalendarChange={(dates) => {
                setRangePickerDates(dates || [null, null]);
              }}
              onChange={(values) => {
                if (!values || !values[0] || !values[1]) {
                  return;
                }
                const [startValue, endValue] = values;
                if (endValue.diff(startValue, 'days') > MAX_DATE_RANGE_DAYS - 1) {
                  message.error(`日期跨度不能超过 ${MAX_DATE_RANGE_DAYS} 天（含起止两端）`);
                  return;
                }
                setFilters(prev => ({
                  ...prev,
                  date_start: startValue.format(DATE_FORMAT),
                  date_end: endValue.format(DATE_FORMAT)
                }));
              }}
            />
          </div>
        </div>
        <div className="filter-bar-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleCreate(null)}
          >
            新增总代理/代理
          </Button>
          <Button icon={<ApartmentOutlined />} onClick={() => loadAgentTree()}>
            刷新结构
          </Button>
        </div>
      </Card>

      <Card
        className="table-card"
        title={`代理流水明细 · ${selectedDateLabel}`}
        extra={<Text type="secondary">打开页面即显示各代理账号在所选区间的团队流水</Text>}
      >
        <Alert
          type="info"
          showIcon
          message="团队流水包含在线支付＋客服确认代充，排除管理员补发及平台币消费"
          description="计佣基数和佣金仍按原现金口径，客服代充本次仅纳入充值展示、不新增返佣。同一条充值会计入所属代理及其上级团队，不同层级行不要直接相加。日期起止同天即为当天，最长30天含起止。"
          style={{ marginBottom: 16 }}
        />
        <Table
          columns={agentFlowColumns}
          dataSource={visibleAgentRows}
          loading={treeLoading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `共 ${total} 个代理账号`
          }}
          size="middle"
          scroll={{ x: 1250 }}
          locale={{ emptyText: renderEmptyPanel('暂无代理流水', `${selectedDateLabel} 当前筛选条件下没有代理数据。`) }}
        />
      </Card>

      <div className="agent-management-workspace">
        <Card
          className="table-card agent-tree-panel"
          title="总代理层级"
          extra={<Text type="secondary">按当前筛选条件展示代理结构，流水口径为 {selectedDateLabel}</Text>}
          loading={treeLoading}
        >
          <div className="agent-tree-panel-body">
            <div className="agent-tree-summary">
              <Tag color="blue">顶层节点 {groupedAgents.length}</Tag>
              <Tag color="green">可见代理 {visibleAgentRows.length}</Tag>
            </div>
            {treeData.length > 0 ? (
              <Tree
                showLine
                defaultExpandAll
                treeData={treeData}
                className="agent-hierarchy-tree"
                selectedKeys={selectedAgentId ? [`agent-${selectedAgentId}`] : []}
                onSelect={handleTreeSelect}
              />
            ) : (
              renderEmptyPanel('暂无代理层级', '当前筛选条件下没有可展示的代理结构。')
            )}
          </div>
        </Card>

        <Card
          className="table-card agent-table-panel"
          title="账号、权限与流水"
          extra={<Text type="secondary">按卡片查看节点、推广、分成和操作</Text>}
          loading={treeLoading}
        >
          {visibleAgentRows.length > 0 ? (
            <div className="agent-card-list">
              {visibleAgentRows.map((record) => (
                <div
                  className={`agent-card-item${String(selectedAgentId) === String(record.id) ? ' agent-card-item-active' : ''}`}
                  key={record.id}
                  ref={(node) => {
                    if (node) {
                      agentCardRefs.current[String(record.id)] = node;
                    } else {
                      delete agentCardRefs.current[String(record.id)];
                    }
                  }}
                >
                  <div className="agent-card-header">
                    <div className="agent-card-heading">
                      <div className="agent-card-title-row">
                        <div className="agent-card-title">{record.agent_name}</div>
                        <Tag color={record.depth === 0 ? 'green' : 'orange'}>
                          {record.depth === 0 ? '总代理' : `${record.depth}级下级`}
                        </Tag>
                        <Tag color={record.status === 1 ? 'green' : 'red'}>
                          {record.status === 1 ? '正常' : '封禁'}
                        </Tag>
                      </div>
                      <div className="agent-card-subtitle">
                        <span>登录账号 {record.username || '-'}</span>
                        <span>代理ID {record.id}</span>
                        <span>{record.parent_name || '总代理'}</span>
                      </div>
                    </div>
                    <div className="agent-card-badges">
                      <Tag color="cyan">{record.commission_rate || 0}% 佣金</Tag>
                      <Tag color="blue">{record.user_count || 0} 名玩家</Tag>
                      <Tag color="purple">{record.sub_agent_count || 0} 个下级</Tag>
                    </div>
                  </div>

                  <div className="agent-card-grid">
                    <div className="agent-card-section">
                      <div className="agent-card-section-label">推广信息</div>
                      <div className="agent-card-code"><Text code className="text-code-sm">{record.invite_code || '-'}</Text></div>
                      <div className="agent-card-meta">联系方式 {record.contact || '未填写'}</div>
                      <div className="agent-card-link-actions">
                        <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => handleCopyCode(record.invite_code)}>
                          邀请码
                        </Button>
                        <Button size="small" type="link" icon={<LinkOutlined />} onClick={() => handleCopyRegisterLink(record.invite_code)}>
                          注册链接
                        </Button>
                      </div>
                    </div>

                    <div className="agent-card-section">
                      <div className="agent-card-section-label">充值流水</div>
                      <div className="agent-card-finance-grid">
                        <div className="agent-card-finance-item">
                          <span>{selectedDateShortLabel}</span>
                          <strong>{formatCurrency(record.selected_day_sales)}</strong>
                        </div>
                        <div className="agent-card-finance-item">
                          <span>昨日</span>
                          <strong>{formatCurrency(record.yesterday_sales)}</strong>
                        </div>
                        <div className="agent-card-finance-item">
                          <span>总流水</span>
                          <strong>{formatCurrency(record.total_sales)}</strong>
                        </div>
                        <div className="agent-card-finance-item agent-card-finance-item-accent">
                          <span>总佣金（原口径）</span>
                          <strong>{formatCurrency(record.total_recharge_after)}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="agent-card-section">
                      <div className="agent-card-section-label">账号权限</div>
                      <div className="agent-card-meta-list">
                        <div><span>创建时间</span><strong>{record.create_time ? new Date(record.create_time).toLocaleString('zh-CN') : '-'}</strong></div>
                        <div><span>上级归属</span><strong>{record.parent_name || '总代理'}</strong></div>
                      </div>
                    </div>
                  </div>

                  {renderAgentActions(record)}
                </div>
              ))}
            </div>
          ) : (
            renderEmptyPanel('暂无代理数据', '当前筛选条件下没有可展示的代理节点。')
          )}
        </Card>
      </div>

      {/* 创建代理弹窗 */}
      <Modal
        title="新增总代理/代理账号"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={() => createForm.submit()}
        width={600}
      >
        <div className="agent-modal-note">
          <div>
            <span className="agent-modal-note-strong">创建后将生成新的代理登录入口。</span>
            这里填写的账号、密码和邀请码会直接用于代理登录和推广注册。
          </div>
        </div>
        <Alert
          message="创建代理时必须设置登录账号和密码"
          description="代理将使用此账号密码登录后台查看自己的数据（只读模式）"
          type="info"
          showIcon
          className="block-gap-md"
        />
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item name="parent_id" label="上级代理">
            <Select placeholder="选择上级代理（留空则为顶级代理）" allowClear>
              {agentOptions.map(agent => (
                <Option key={agent.id} value={agent.id}>{agent.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="登录账号"
                rules={[
                  { required: true, message: '请输入登录账号!' },
                  { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线' }
                ]}
              >
                <Input placeholder="请输入登录账号" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label="初始密码"
                rules={[
                  { required: true, message: '请输入初始密码!' },
                  { min: 6, message: '密码长度不能少于6位!' }
                ]}
                extra="至少6位字符，创建后可重置"
              >
                <Input.Password placeholder="请输入初始密码" prefix={<LockOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="second_password"
                label="二级密码"
                extra="明文存储，用于代理修改密码时二次确认"
              >
                <Input.Password placeholder="请输入二级密码（可选）" prefix={<LockOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agent_name"
                label="代理名称"
                rules={[{ required: true, message: '请输入代理名称' }]}
              >
                <Input placeholder="请输入代理名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="commission_rate"
                label="佣金比例(%)"
                initialValue={0}
                rules={[
                  { required: true, message: '请输入佣金比例!' },
                  { type: 'number', min: 0, max: 100, message: '佣金比例必须在 0-100 之间!' }
                ]}
                extra="佣金比例范围：0-100%"
              >
                <InputNumber
                  placeholder="请输入佣金比例"
                  min={0}
                  max={100}
                  step={0.1}
                  precision={1}
                  className="field-width-full"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="invite_code"
                label="邀请码"
                rules={[{ required: true, message: '请输入邀请码' }]}
                extra={
                  <Button
                    size="small"
                    type="link"
                    onClick={handleGenerateCode}
                    className="inline-link-button"
                  >
                    自动生成
                  </Button>
                }
              >
                <Input placeholder="请输入邀请码或点击自动生成" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item name="contact" label="联系方式">
            <Input placeholder="请输入联系方式" />
          </Form.Item>
          
          <Form.Item name="remark" label="备注">
            <Input placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑代理弹窗 */}
      <Modal
        title="编辑代理账号"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => editForm.submit()}
        width={600}
      >
        <div className="agent-modal-note">
          <div>
            <span className="agent-modal-note-strong">编辑会直接影响代理登录和推广链路。</span>
            建议优先核对登录账号、邀请码和佣金比例，再决定是否重置密码或切换状态。
          </div>
        </div>
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="登录账号"
                rules={[
                  { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线' }
                ]}
                extra="修改后代理需要使用新账号登录"
              >
                <Input placeholder="请输入登录账号" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label="重置密码"
                rules={[
                  { min: 6, message: '密码长度不能少于6位!' }
                ]}
                extra="留空则不修改，填写则重置为新密码"
              >
                <Input.Password placeholder="留空不修改" prefix={<LockOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="second_password"
                label="二级密码"
                extra="明文存储，用于代理修改密码时二次确认"
              >
                <Input.Password placeholder="请输入二级密码（可选）" prefix={<LockOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agent_name"
                label="代理名称"
                rules={[{ required: true, message: '请输入代理名称' }]}
              >
                <Input placeholder="请输入代理名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="commission_rate"
                label="佣金比例(%)"
                rules={[
                  { type: 'number', min: 0, max: 100, message: '佣金比例必须在 0-100 之间!' }
                ]}
                extra="佣金比例范围：0-100%"
              >
                <InputNumber
                  placeholder="请输入佣金比例"
                  min={0}
                  max={100}
                  step={0.1}
                  precision={1}
                  className="field-width-full"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="invite_code"
                label="邀请码"
                rules={[{ required: true, message: '请输入邀请码' }]}
              >
                <Input placeholder="请输入邀请码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact" label="联系方式">
                <Input placeholder="请输入联系方式" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态"
                valuePropName="checked"
              >
                <Switch checkedChildren="活跃" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remark" label="备注">
                <Input placeholder="请输入备注" />
              </Form.Item>
            </Col>
          </Row>

          {/* 人工代充(需求一): 客服身份/聊天昵称/共享额度。仅新增字段, 不影响其它逻辑与日期范围区域。 */}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="人工代充设置"
            description="开启“客服身份”后该代理可在其工作台使用代充会话功能；“共享额度”开启后，允许我的下级客服在其余额不足时逐级借用我的余额（上级视角，逐级向上遇到未开启共享的上级即停止）。"
          />
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="is_recharge_agent"
                label="客服身份"
                valuePropName="checked"
                extra="是否可接人工代充订单"
              >
                <Switch checkedChildren="客服" unCheckedChildren="非客服" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="share_quota"
                label="共享额度"
                valuePropName="checked"
                extra="开启后，允许我的下级客服在其余额不足时逐级借用我的余额"
              >
                <Switch checkedChildren="共享" unCheckedChildren="独立" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="chat_nickname" label="聊天昵称" extra="玩家聊天窗展示名">
                <Input placeholder="留空则用代理名称" maxLength={64} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 代理详情弹窗 */}
      <Modal
        title="代理账号详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
      >
        {agentDetail && (
          <>
            <div className="detail-stat-grid">
              <div className="detail-stat-card">
                <div className="detail-stat-label">绑定用户</div>
                <div className="detail-stat-value">{agentDetail.users?.length || 0}</div>
              </div>
              <div className="detail-stat-card">
                <div className="detail-stat-label">下级代理</div>
                <div className="detail-stat-value">{agentDetail.subAgents?.length || 0}</div>
              </div>
              <div className="detail-stat-card">
                <div className="detail-stat-label">{selectedDateLabel} 流水</div>
                <div className="detail-stat-value">{formatCurrency(getSalesAmount(agentDetail.agent, 'selected_day'))}</div>
              </div>
              <div className="detail-stat-card">
                <div className="detail-stat-label">总流水</div>
                <div className="detail-stat-value">{formatCurrency(getSalesAmount(agentDetail.agent, 'total'))}</div>
              </div>
            </div>

            <div className="detail-section block-top-md">
              <div className="detail-section-head">
                <div>
                  <div className="detail-section-title">代理信息</div>
                  <div className="detail-section-subtitle">查看登录账号、邀请码、状态和联系信息。</div>
                </div>
              </div>
              <div className="selection-panel">
                <div className="selection-list">
                  <div className="selection-item">
                    <div className="selection-item-main">
                      <div className="selection-panel-title">{agentDetail.agent.agent_name}</div>
                      <div className="selection-panel-subtitle">代理 ID {agentDetail.agent.id}</div>
                    </div>
                    <Tag color={agentDetail.agent.status === 1 ? 'green' : 'red'}>
                      {agentDetail.agent.status === 1 ? '正常' : '封禁'}
                    </Tag>
                  </div>
                  <div className="selection-item">
                    <div className="selection-item-main">
                      <div className="selection-panel-title">{agentDetail.agent.username || '-'}</div>
                      <div className="selection-panel-subtitle">登录账号</div>
                    </div>
                    <Text code>{agentDetail.agent.second_password || '未设置二级密码'}</Text>
                  </div>
                  <div className="selection-item">
                    <div className="selection-item-main">
                      <div className="selection-panel-title"><Text code>{agentDetail.agent.invite_code}</Text></div>
                      <div className="selection-panel-subtitle">邀请码</div>
                    </div>
                    <Button type="link" icon={<CopyOutlined />} onClick={() => handleCopyCode(agentDetail.agent.invite_code)}>
                      复制邀请码
                    </Button>
                  </div>
                  <div className="selection-item">
                    <div className="selection-item-main">
                      <div className="selection-panel-title">{agentDetail.agent.contact || '-'}</div>
                      <div className="selection-panel-subtitle">联系方式</div>
                    </div>
                    <Tag color="cyan">{agentDetail.agent.commission_rate || 0}% 佣金</Tag>
                  </div>
                  <div className="selection-item">
                    <div className="selection-item-main">
                      <div className="selection-panel-title">{agentDetail.agent.create_time ? new Date(agentDetail.agent.create_time).toLocaleString('zh-CN') : '-'}</div>
                      <div className="selection-panel-subtitle">创建时间</div>
                    </div>
                    <span>{agentDetail.users?.length || 0} 人绑定</span>
                  </div>
                  <div className="selection-item">
                    <div className="selection-item-main">
                      <div className="selection-panel-title">{agentDetail.agent.remark || '-'}</div>
                      <div className="selection-panel-subtitle">备注</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-section block-top-md">
              <div className="detail-section-head">
                <div>
                  <div className="detail-section-title">佣金明细</div>
                  <div className="detail-section-subtitle">以下为原现金口径的计佣基数及佣金；不包含本次新增到充值展示的客服代充。</div>
                </div>
              </div>
              <div className="detail-stat-grid">
                <div className="detail-stat-card">
                  <div className="detail-stat-label">{selectedDateLabel} 计佣基数</div>
                  <div className="detail-stat-value">{formatCurrency(agentDetail.agent.selected_day_recharge || 0)}</div>
                  <div className="table-cell-secondary">佣金后 {formatCurrency((agentDetail.agent.selected_day_recharge || 0) * (agentDetail.agent.commission_rate || 0) / 100)}</div>
                </div>
                <div className="detail-stat-card">
                  <div className="detail-stat-label">昨日计佣基数</div>
                  <div className="detail-stat-value">{formatCurrency(agentDetail.agent.yesterday_recharge || 0)}</div>
                  <div className="table-cell-secondary">佣金后 {formatCurrency((agentDetail.agent.yesterday_recharge || 0) * (agentDetail.agent.commission_rate || 0) / 100)}</div>
                </div>
                <div className="detail-stat-card">
                  <div className="detail-stat-label">累计计佣基数</div>
                  <div className="detail-stat-value">{formatCurrency(agentDetail.agent.total_recharge || 0)}</div>
                  <div className="table-cell-secondary">佣金后 {formatCurrency((agentDetail.agent.total_recharge || 0) * (agentDetail.agent.commission_rate || 0) / 100)}</div>
                </div>
                <div className="detail-stat-card">
                  <div className="detail-stat-label">佣金比例</div>
                  <div className="detail-stat-value">{agentDetail.agent.commission_rate || 0}%</div>
                  <div className="table-cell-secondary">当前分佣规则</div>
                </div>
              </div>
            </div>

            <Collapse defaultActiveKey={[]} className="block-top-md">
              <Collapse.Panel header={`绑定用户 (${agentDetail.users?.length || 0})`} key="users">
                <Table
                  columns={detailColumns}
                  dataSource={agentDetail.users || []}
                  pagination={false}
                  rowKey="user_id"
                  size="small"
                  locale={{ emptyText: renderEmptyPanel('暂无绑定用户', '当前代理还没有绑定玩家。') }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div className="selection-panel">
                        <div className="selection-list">
                          <div className="selection-item">
                            <div className="selection-item-main">
                              <div className="selection-panel-title">{record.game_name || record.user_name || '-'}</div>
                              <div className="selection-panel-subtitle">用户ID {record.user_id} / 游戏ID {record.game_user_id || '-'}</div>
                            </div>
                            <Tag color="blue">VIP {record.vip_lv || 0}</Tag>
                          </div>
                          <div className="selection-item">
                            <div className="selection-item-main">
                              <div className="selection-panel-title">{record.nick_name || '-'}</div>
                              <div className="selection-panel-subtitle">昵称</div>
                            </div>
                            <span>等级 {record.level || 0}</span>
                          </div>
                          <div className="selection-item">
                            <div className="selection-item-main">
                              <div className="selection-panel-title">{record.power || 0}</div>
                              <div className="selection-panel-subtitle">战力</div>
                            </div>
                            <span>{record.bind_time ? new Date(record.bind_time).toLocaleString('zh-CN') : '未记录绑定时间'}</span>
                          </div>
                        </div>
                      </div>
                    ),
                    defaultExpandAllRows: false,
                    rowExpandable: () => true
                  }}
                />
              </Collapse.Panel>

              <Collapse.Panel header={`下级代理 (${agentDetail.subAgents?.length || 0})`} key="subAgents">
                <Table
                  columns={subAgentDetailColumns}
                  dataSource={agentDetail.subAgents || []}
                  pagination={false}
                  rowKey="id"
                  size="small"
                  locale={{ emptyText: renderEmptyPanel('暂无下级代理', '当前代理还没有下级代理节点。') }}
                  expandable={{
                    defaultExpandAllRows: false,
                    rowExpandable: (record) => record.children && record.children.length > 0
                  }}
                />
              </Collapse.Panel>

              <Collapse.Panel header={`充值记录 (${agentDetail.rechargeLogs?.length || 0})`} key="rechargeLogs">
                <Table
                  columns={rechargeDetailColumns}
                  dataSource={agentDetail.rechargeLogs || []}
                  pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 条充值记录` }}
                  rowKey={(record) => record.id || record.order_no}
                  size="small"
                  scroll={{ x: 1100 }}
                  locale={{ emptyText: renderEmptyPanel('暂无充值记录', '当前代理还没有关联的充值流水。') }}
                />
              </Collapse.Panel>
            </Collapse>
          </>
        )}
      </Modal>
    </div>
  );
}

export default AgentManagement;
